import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "../../../utils/analytics";
import { isReminderPromptBlocked } from "../reminderPromptBlockers";
import {
  markReminderPromptDismissed,
  markReminderPromptShown,
} from "../reminderPromptStorage";

const SCROLL_THRESHOLD_PX = 40;

const hasReachedScrollThreshold = () => {
  if (window.scrollY >= SCROLL_THRESHOLD_PX) return true;

  const main = document.querySelector("main");
  if (main && main.scrollTop >= SCROLL_THRESHOLD_PX) return true;

  const scrollables = document.querySelectorAll("[data-reminder-scroll-root]");
  for (const el of scrollables) {
    if (el.scrollTop >= SCROLL_THRESHOLD_PX) return true;
  }

  return false;
};

export const useReminderSetupPrompt = ({ enabled }) => {
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  const dismissedRef = useRef(false);

  openRef.current = open;

  const dismiss = useCallback(() => {
    markReminderPromptDismissed();
    dismissedRef.current = true;
    setOpen(false);
    trackEvent("reminder_prompt_dismiss");
  }, []);

  const close = useCallback(() => {
    dismissedRef.current = true;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open || enabled) return undefined;
    setOpen(false);
    return undefined;
  }, [enabled, open]);

  useEffect(() => {
    if (!enabled) {
      dismissedRef.current = false;
      return undefined;
    }

    let cancelled = false;

    const tryShow = () => {
      if (cancelled || openRef.current || dismissedRef.current) return;
      if (!hasReachedScrollThreshold()) return;
      if (isReminderPromptBlocked()) return;

      markReminderPromptShown();
      setOpen(true);
      trackEvent("reminder_prompt_shown");
    };

    const onScroll = () => {
      tryShow();
    };

    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    tryShow();

    return () => {
      cancelled = true;
      document.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [enabled]);

  return {
    open,
    setOpen,
    dismiss,
    close,
  };
};
